import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {Mail, Send} from 'lucide-react';
import { getCachedText, preloadSiteContent } from '@/hooks/useSiteContent'

export default function Contact() {
  const location = useLocation();
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    sujet: 'information',
    message: ''
  });
  const [envoye, setEnvoye] = useState(false);
  const [, setTick] = useState(0)

  useEffect(() => {
    preloadSiteContent()
    const handler = () => setTick((n) => n + 1)
    window.addEventListener('site-content-updated', handler)
    return () => window.removeEventListener('site-content-updated', handler)
  }, [])

  const t = (key: string, fallback: string) => getCachedText('contact', key, fallback)

  // Pré-remplissage automatique via querystring
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const intention = params.get('intention');
    const offre = params.get('offre');

    if (!intention && !offre) return;

    let sujet = 'information';
    let message = '';

    if (intention === 'organiser') {
      sujet = 'organiser';
      message = offre
        ? `Bonjour,\n\nJe souhaite organiser l'accompagnement « ${offre} » dans mon lieu / ma structure.\n\nVoici quelques informations sur le contexte :\n- Lieu envisagé : \n- Période souhaitée : \n- Nombre de participants estimé : \n\nPouvons-nous échanger ?`
        : `Bonjour,\n\nJe souhaite organiser un accompagnement dans mon lieu / ma structure.\n\nPouvons-nous échanger ?`;
    } else if (intention === 'exposer') {
      sujet = 'exposer';
      message = "Bonjour,\n\nJe souhaite candidater pour exposer dans la galerie du Sanctuaire.\n\nVoici quelques mots sur mon univers :\n";
    } else if (intention === 'rdv') {
      sujet = 'rdv';
      message = "Bonjour,\n\nJe souhaite prendre rendez-vous pour :\n\n";
    } else if (intention === 'individuelle') {
      sujet = 'individuelle';
    } else if (intention === 'immersive') {
      sujet = 'immersive';
    }

    setFormData((prev) => ({ ...prev, sujet, message: message || prev.message }));

    // Scroll vers le formulaire
    setTimeout(() => {
      const form = document.getElementById('contact-form');
      if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  }, [location.search]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEnvoye(true);
    setTimeout(() => {
      setEnvoye(false);
      setFormData({ nom: '', email: '', sujet: 'information', message: '' });
    }, 4000);
  };

  return (
    <div className="fade-in">
      {/* Intro */}
      <section className="px-6 py-24 md:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs tracking-[0.4em] uppercase text-[#c9a96e] mb-8">
            {t('eyebrow', '— Entrer en lien —')}
          </p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-[#f5f1ea] mb-10">
            {t('page_title', 'Contact')}
          </h1>
          <div className="w-16 h-px bg-[#c9a96e] mx-auto mb-10" />
          <p className="font-serif text-xl md:text-2xl text-[#f5f1ea]/80 leading-loose italic whitespace-pre-line">
            {t('page_intro', 'Un message, une intention,\nun temps pour se rencontrer.')}
          </p>
        </div>
      </section>

      {/* Form */}
      <section id="contact-form" className="px-6 pb-24">
        <div className="max-w-2xl mx-auto">
          {envoye ? (
            <div className="text-center py-20 border border-[#c9a96e]/30 p-12">
              <div className="w-16 h-16 rounded-full border border-[#c9a96e] flex items-center justify-center mx-auto mb-8 breathe">
                <div className="w-2 h-2 rounded-full bg-[#c9a96e]" />
              </div>
              <p className="font-serif text-2xl text-[#c9a96e] italic mb-4">
                {t('sent_title', 'Le message a été reçu.')}
              </p>
              <p className="text-sm text-[#a8a39e] font-light">
                {t('sent_sub', 'Une réponse viendra, dans un temps juste.')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-3">
                  {t('label_nom', 'Nom')}
                </label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  className="w-full bg-transparent border-b border-[#c9a96e]/30 focus:border-[#c9a96e] py-3 text-[#f5f1ea] font-light outline-none transition-colors duration-500"
                />
              </div>

              <div>
                <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-3">
                  {t('label_email', 'Email')}
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-transparent border-b border-[#c9a96e]/30 focus:border-[#c9a96e] py-3 text-[#f5f1ea] font-light outline-none transition-colors duration-500"
                />
              </div>

              <div>
                <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-3">
                  {t('label_intention', 'Intention')}
                </label>
                <select
                  value={formData.sujet}
                  onChange={(e) => setFormData({ ...formData, sujet: e.target.value })}
                  className="w-full bg-[#262626] border-b border-[#c9a96e]/30 focus:border-[#c9a96e] py-3 text-[#f5f1ea] font-light outline-none transition-colors duration-500"
                >
                  <option value="information">{t('option_information', 'Simple prise de contact')}</option>
                  <option value="individuelle">{t('option_individuelle', 'Réservation séance individuelle')}</option>
                  <option value="immersive">{t('option_immersive', 'Réservation expérience immersive')}</option>
                  <option value="organiser">{t('option_organiser', 'Organiser un accompagnement chez moi')}</option>
                  <option value="exposer">{t('option_exposer', 'Exposer dans la galerie')}</option>
                  <option value="rdv">{t('option_rdv', 'Prendre rendez-vous au Sanctuaire')}</option>
                  <option value="autre">{t('option_autre', 'Autre')}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-3">
                  {t('label_message', 'Message')}
                </label>
                <textarea
                  required
                  rows={8}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full bg-transparent border border-[#c9a96e]/20 focus:border-[#c9a96e]/60 p-4 text-[#f5f1ea] font-light outline-none transition-colors duration-500 leading-relaxed resize-none"
                  placeholder={t('placeholder_message', 'Prends le temps nécessaire pour exprimer ce que tu souhaites partager...')}
                />
              </div>

              <div className="pt-6">
                <button
                  type="submit"
                  className="group inline-flex items-center gap-4 px-10 py-4 border border-[#c9a96e]/50 hover:border-[#c9a96e] text-xs tracking-[0.3em] uppercase text-[#c9a96e] hover:bg-[#c9a96e]/5 transition-all duration-500"
                >
                  <span>{t('submit_label', 'Envoyer')}</span>
                  <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Direct contact */}
      <section className="px-6 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-2xl mx-auto text-center space-y-10">
          <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]">{t('direct_eyebrow', 'Contact direct')}</p>

          <a
            href={`mailto:${t('email_address', 'contact@laboratoiredti.ch')}`}
            className="group inline-flex items-center gap-4 font-serif text-2xl md:text-3xl text-[#f5f1ea] hover:text-[#c9a96e] transition-colors duration-700"
          >
            <Mail className="w-6 h-6 text-[#c9a96e]" />
            <span>{t('email_address', 'contact@laboratoiredti.ch')}</span>
          </a>

          <div className="w-16 h-px bg-[#c9a96e]/40 mx-auto" />

          <p className="font-serif italic text-lg text-[#f5f1ea]/70 leading-loose whitespace-pre-line">
            {t('direct_text', 'Les échanges se font dans un rythme calme.\nChaque message reçoit une réponse attentive.')}
          </p>
        </div>
      </section>
    </div>
  );
}
